import { pointsBounds } from './geometry.ts'
import type {
  EllipseNode,
  ImageNode,
  LineNode,
  NodeId,
  PathNode,
  Point,
  Rect,
  RectNode,
  TextNode,
} from './types.ts'

export const defaults = {
  fill: '#ffffff',
  stroke: '#1d1d20',
  strokeWidth: 2,
  cornerRadius: 0,
  inkColor: '#1d1d20',
  inkWidth: 3,
  textColor: '#1d1d20',
  fontSize: 24,
  fontFamily:
    'ui-sans-serif, system-ui, -apple-system, "Segoe UI", Roboto, sans-serif',
}

export function newId(): NodeId {
  if (typeof crypto.randomUUID === 'function') return crypto.randomUUID()
  // Non-secure contexts have no randomUUID; this only needs to be collision-free.
  return `n${Date.now().toString(36)}${Math.random().toString(36).slice(2, 10)}`
}

const base = (r: Rect) => ({
  id: newId(),
  x: r.x,
  y: r.y,
  w: r.w,
  h: r.h,
  rotation: 0,
  opacity: 1,
  locked: false,
  hidden: false,
})

export function createRect(r: Rect): RectNode {
  return {
    ...base(r),
    type: 'rect',
    fill: defaults.fill,
    stroke: defaults.stroke,
    strokeWidth: defaults.strokeWidth,
    cornerRadius: defaults.cornerRadius,
  }
}

export function createEllipse(r: Rect): EllipseNode {
  return {
    ...base(r),
    type: 'ellipse',
    fill: defaults.fill,
    stroke: defaults.stroke,
    strokeWidth: defaults.strokeWidth,
  }
}

/**
 * Lines and paths are stored as local points in a fixed viewBox. A degenerate
 * axis would collapse the viewBox, so both extents are floored at 1.
 */
function localizePoints(worldPoints: readonly number[]) {
  const b = pointsBounds(worldPoints)
  const local: number[] = []
  for (let i = 0; i + 1 < worldPoints.length; i += 2) {
    local.push(worldPoints[i]! - b.x, worldPoints[i + 1]! - b.y)
  }
  return { bounds: b, local, viewW: Math.max(1, b.w), viewH: Math.max(1, b.h) }
}

/** A line chain from world-space points, flat [x,y,...]. */
export function createPolyline(worldPoints: readonly number[], closed: boolean): LineNode {
  const { bounds, local, viewW, viewH } = localizePoints(worldPoints)
  return {
    ...base({ x: bounds.x, y: bounds.y, w: Math.max(1, bounds.w), h: Math.max(1, bounds.h) }),
    type: 'line',
    points: local,
    viewW,
    viewH,
    closed,
    stroke: defaults.stroke,
    strokeWidth: defaults.strokeWidth,
  }
}

export function createPath(
  worldPoints: readonly number[],
  stroke = defaults.inkColor,
  strokeWidth = defaults.inkWidth,
): PathNode {
  const { bounds, local, viewW, viewH } = localizePoints(worldPoints)
  return {
    ...base({ x: bounds.x, y: bounds.y, w: Math.max(1, bounds.w), h: Math.max(1, bounds.h) }),
    type: 'path',
    points: local,
    viewW,
    viewH,
    stroke,
    strokeWidth,
  }
}

export function createText(at: Point, text = ''): TextNode {
  return {
    ...base({ x: at.x, y: at.y, w: 1, h: defaults.fontSize * 1.3 }),
    type: 'text',
    text,
    fontSize: defaults.fontSize,
    fontFamily: defaults.fontFamily,
    color: defaults.textColor,
    align: 'left',
    autoWidth: true,
  }
}

export function createImage(
  r: Rect,
  assetId: string,
  naturalW: number,
  naturalH: number,
): ImageNode {
  return { ...base(r), type: 'image', assetId, naturalW, naturalH }
}
